import React, { useCallback, useEffect, useState } from 'react';
import { useConfirm } from '../../components/ConfirmDialog';
import PropTypes from 'prop-types';
import { CheckCircleIcon, ShieldCheckIcon } from '@heroicons/react/outline';
import API from '../../utils/api';
import SsoButtons from '../../components/SsoButtons';
import { PROVIDER_LABELS, ssoProviders } from '../../utils/sso';

// Keep the cached session in sync so banners/nav reflect the change immediately.
function updateStoredUser(changes) {
    try {
        const stored = JSON.parse(localStorage.getItem('user') || '{}');
        localStorage.setItem('user', JSON.stringify({ ...stored, ...changes }));
    } catch (e) { /* ignore */ }
}

const SignInMethods = ({ onPasswordLoginChange }) => {
    const confirm = useConfirm();
    const [status, setStatus] = useState(null);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        const data = await API.getData('auth/identities');
        if (data && Array.isArray(data.identities)) {
            setStatus(data);
            updateStoredUser({
                linkedProviders: data.identities.map((i) => i.provider),
                passwordLoginAllowed: data.passwordLoginAllowed,
            });
            if (onPasswordLoginChange) onPasswordLoginChange(data.passwordLoginAllowed);
        } else {
            setMessage({ type: 'error', text: 'Could not load your sign-in methods.' });
        }
    }, [onPasswordLoginChange]);

    useEffect(() => { load(); }, [load]);

    const run = async (request, successText) => {
        setBusy(true);
        setMessage({ type: '', text: '' });
        try {
            // The API client resolves with the body on success and with the
            // axios error response ({ status, data }) on failure.
            const result = await request();
            if (!result || (typeof result.status === 'number' && result.status >= 400)) {
                setMessage({ type: 'error', text: result?.data?.msg || 'Something went wrong. Please try again.' });
            } else {
                setMessage({ type: 'success', text: successText });
            }
            await load();
        } finally {
            setBusy(false);
        }
    };

    const handleLink = (provider, idToken) =>
        run(() => API.postData(`auth/identities/${provider}`, { idToken }), `${PROVIDER_LABELS[provider]} is now linked. You can use it to sign in.`);

    const handleUnlink = async (provider) => {
        const ok = await confirm({
            title: `Unlink ${PROVIDER_LABELS[provider]}?`,
            message: `You won't be able to sign in with ${PROVIDER_LABELS[provider]} any more until you link it again.`,
            confirmLabel: 'Unlink',
            tone: 'danger',
        });
        if (!ok) return;
        run(() => API.deleteData(`auth/identities/${provider}`), `${PROVIDER_LABELS[provider]} was unlinked.`);
    };

    const handlePasswordToggle = async (disabled) => {
        if (disabled) {
            const ok = await confirm({
                title: 'Turn off password sign-in?',
                message: 'From now on you will sign in with Google or Apple only.',
                confirmLabel: 'Turn off',
                tone: 'danger',
            });
            if (!ok) return;
        }
        run(() => API.putData('auth/password-login', { disabled }),
            disabled ? 'Password sign-in is off for your account.' : 'Password sign-in is back on for your account.');
    };

    if (!status) {
        return <div className="mt-6 rounded-lg bg-white p-6 text-sm text-gray-500 shadow">Loading sign-in methods…</div>;
    }

    const linked = Object.fromEntries(status.identities.map((i) => [i.provider, i]));
    const configured = ssoProviders();
    const unlinkedProviders = configured.filter((p) => !linked[p]);
    const hasSso = status.identities.length > 0;
    const globallyDisabled = status.passwordLoginMode === 'disabled'
        || (status.passwordLoginMode === 'unlinked_only' && hasSso);

    return (
        <div className="mt-6 max-w-2xl space-y-6 rounded-lg bg-white p-6 shadow">
            {message.text && (
                <div
                    role={message.type === 'error' ? 'alert' : 'status'}
                    className={`rounded-md px-4 py-3 text-sm ${message.type === 'error' ? 'bg-red-50 text-red-700' : 'bg-emerald-50 text-emerald-800'}`}
                >
                    {message.text}
                </div>
            )}

            <section>
                <h2 className="text-lg font-semibold text-gray-900">Google & Apple</h2>
                <p className="mt-1 text-sm text-gray-500">
                    Signing in with Google or Apple is more secure than a password and means one less password to remember.
                </p>

                <ul className="mt-4 divide-y divide-gray-100 rounded-md border border-gray-200">
                    {configured.map((provider) => (
                        <li key={provider} className="flex items-center justify-between gap-4 px-4 py-3">
                            <div className="min-w-0">
                                <p className="font-medium text-gray-900">{PROVIDER_LABELS[provider]}</p>
                                <p className="truncate text-sm text-gray-500">
                                    {linked[provider] ? (linked[provider].email || 'Linked') : 'Not linked'}
                                </p>
                            </div>
                            {linked[provider] ? (
                                <div className="flex shrink-0 items-center gap-3">
                                    <span className="flex items-center gap-1 text-sm font-medium text-emerald-700">
                                        <CheckCircleIcon className="h-5 w-5" /> Linked
                                    </span>
                                    <button
                                        type="button"
                                        disabled={busy}
                                        onClick={() => handleUnlink(provider)}
                                        className="text-sm font-medium text-red-600 hover:text-red-800 disabled:opacity-50"
                                    >
                                        Unlink
                                    </button>
                                </div>
                            ) : null}
                        </li>
                    ))}
                </ul>

                {unlinkedProviders.length > 0 && (
                    <div className="mt-4 max-w-sm">
                        <p className="mb-2 text-sm font-medium text-gray-700">Link an account</p>
                        <SsoButtons
                            mode="link"
                            providers={unlinkedProviders}
                            onToken={handleLink}
                            onError={(text) => setMessage({ type: 'error', text })}
                            disabled={busy}
                        />
                    </div>
                )}
                {configured.length === 0 && (
                    <p className="mt-4 text-sm text-gray-500">Google and Apple sign-in aren't set up yet.</p>
                )}
            </section>

            <section className="border-t border-gray-100 pt-6">
                <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
                    <ShieldCheckIcon className="h-5 w-5 text-gray-500" /> Password sign-in
                </h2>
                {globallyDisabled ? (
                    <p className="mt-1 text-sm text-gray-500">
                        Password sign-in has been retired for your account by your church admin. Use Google or Apple to sign in.
                    </p>
                ) : !status.hasPassword ? (
                    <p className="mt-1 text-sm text-gray-500">Your account has no password. You sign in with Google or Apple.</p>
                ) : status.passwordLoginDisabled ? (
                    <div className="mt-2 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <p className="text-sm text-gray-500">Off. You sign in with Google or Apple only.</p>
                        <button
                            type="button"
                            disabled={busy}
                            onClick={() => handlePasswordToggle(false)}
                            className="self-start rounded-md px-3 py-1.5 text-sm font-medium text-gray-700 ring-1 ring-gray-300 hover:bg-gray-50 disabled:opacity-50"
                        >
                            Turn password back on
                        </button>
                    </div>
                ) : (
                    <div className="mt-2 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <p className="text-sm text-gray-500">
                            {hasSso
                                ? "On. Now that Google or Apple is linked, you can turn passwords off."
                                : 'On. Link Google or Apple first, then you can turn passwords off.'}
                        </p>
                        <button
                            type="button"
                            disabled={busy || !hasSso}
                            onClick={() => handlePasswordToggle(true)}
                            className="self-start rounded-md bg-emerald-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
                        >
                            Turn off password sign-in
                        </button>
                    </div>
                )}
            </section>
        </div>
    );
};

SignInMethods.propTypes = {
    onPasswordLoginChange: PropTypes.func,
};

SignInMethods.defaultProps = {
    onPasswordLoginChange: undefined,
};

export default SignInMethods;
